import { useState, type ReactElement, type ReactNode } from 'react';

import { Header } from '../components/header.tsx';
import { ScenarioSelect, type ScenarioOption } from '../components/scenario-select.tsx';
import '../styles/styleguide.css';

/**
 * The design system reference, built before any product UI. Every colour,
 * type step, and component state the product uses is on this page, so the
 * product is assembled from parts that already agree with each other. The
 * subject is a diagnostic instrument: paper and ink neutrals, hairline
 * rules, one signal blue reserved for stream state, one fault red, one hold
 * amber.
 */

const SURFACE_TOKENS = [
  '--case',
  '--paper',
  '--paper-sunk',
  '--paper-raised',
  '--rule',
  '--rule-strong',
] as const;
const INK_TOKENS = ['--ink', '--ink-muted', '--ink-faint'] as const;
const STATE_TOKENS = [
  '--signal',
  '--signal-sunk',
  '--fault',
  '--fault-sunk',
  '--hold',
  '--hold-sunk',
] as const;

const TYPE_STEPS = [
  { token: '--step-3', use: 'page title' },
  { token: '--step-2', use: 'section heading' },
  { token: '--step-1', use: 'panel heading' },
  { token: '--step-0', use: 'body prose' },
  { token: '--step--1', use: 'labels, values, controls' },
] as const;

const SPACE_STEPS = ['--s-1', '--s-2', '--s-3', '--s-4', '--s-5', '--s-6', '--s-7', '--s-8'];

const DEMO_SCENARIOS: ScenarioOption[] = [
  { id: 'chaos', proves: 'Seeded random fault injection recovers cleanly' },
  { id: 'happy_path', proves: 'A clean stream start to finish, no faults' },
  { id: 'resume_mid_stream', proves: 'A dropped socket resumes from the last event id' },
];

interface PhaseBadge {
  phase: string;
  tone: 'signal' | 'hold' | 'fault' | 'quiet';
}

const PHASE_BADGES: PhaseBadge[] = [
  { phase: 'idle', tone: 'quiet' },
  { phase: 'connecting', tone: 'hold' },
  { phase: 'queued', tone: 'hold' },
  { phase: 'thinking', tone: 'signal' },
  { phase: 'streaming', tone: 'signal' },
  { phase: 'reconnecting', tone: 'hold' },
  { phase: 'stalled', tone: 'hold' },
  { phase: 'done', tone: 'quiet' },
  { phase: 'error', tone: 'fault' },
  { phase: 'cancelled', tone: 'quiet' },
];

interface ErrorPreview {
  code: string;
  copy: string;
  retry: 'auto' | 'retry' | 'none' | 'restart';
}

/** The ten treatments, copy verbatim from the specification. */
const ERROR_PREVIEWS: ErrorPreview[] = [
  { code: 'rate_limited', copy: 'Too many requests. Retrying in 12 seconds.', retry: 'auto' },
  {
    code: 'context_too_large',
    copy: 'This conversation is too long to continue. Start a new one, or remove earlier messages.',
    retry: 'none',
  },
  {
    code: 'content_blocked',
    copy: 'The response was withheld by a content check. Categories: harmful.',
    retry: 'none',
  },
  {
    code: 'upstream_unavailable',
    copy: 'The model is not reachable right now. Retry, or try again shortly.',
    retry: 'retry',
  },
  {
    code: 'deadline_exceeded',
    copy: 'The response took too long and was cut short. The partial text is kept below.',
    retry: 'retry',
  },
  {
    code: 'stalled',
    copy: 'No output for 20 seconds. The connection is open but nothing is arriving.',
    retry: 'retry',
  },
  { code: 'stream_aborted', copy: 'Stopped. The partial response is kept below.', retry: 'retry' },
  {
    code: 'stream_expired',
    copy: 'The stream can no longer be resumed. Too much time passed, so the buffered output was released.',
    retry: 'restart',
  },
  { code: 'internal_error', copy: 'The server failed while generating. Retry.', retry: 'retry' },
  {
    code: 'unknown',
    copy: 'The stream ended with an unrecognised error: quantum_flux.',
    retry: 'retry',
  },
];

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <section className="styleguide__section">
      <h2 className="styleguide__section-heading">{title}</h2>
      {children}
    </section>
  );
}

function Swatch({ token }: { token: string }): ReactElement {
  return (
    <div className="styleguide__swatch">
      <div className="styleguide__swatch-fill" style={{ background: `var(${token})` }} />
      <span className="mono small">{token}</span>
    </div>
  );
}

function SwatchRow({ tokens }: { tokens: readonly string[] }): ReactElement {
  return (
    <div className="styleguide__swatch-row">
      {tokens.map((token) => (
        <Swatch key={token} token={token} />
      ))}
    </div>
  );
}

function ErrorNotice({ preview }: { preview: ErrorPreview }): ReactElement {
  return (
    <div className="notice notice--fault" role="presentation">
      <div className="styleguide__notice-head">
        <span className="notice__title mono">{preview.code}</span>
        {preview.retry === 'auto' && <span className="mono muted">retrying</span>}
      </div>
      <p>{preview.copy}</p>
      {preview.retry === 'retry' && (
        <div>
          <button type="button" className="button">
            Retry
          </button>
        </div>
      )}
      {preview.retry === 'restart' && (
        <div>
          <button type="button" className="button">
            Start a new stream
          </button>
        </div>
      )}
    </div>
  );
}

export function Styleguide({
  route,
  onNavigate,
}: {
  route: string;
  onNavigate: (path: string) => void;
}): ReactElement {
  const [switchOn, setSwitchOn] = useState(true);
  const [rangeValue, setRangeValue] = useState(60);
  const [scenarioValue, setScenarioValue] = useState('chaos');

  return (
    <>
      <Header route={route} onNavigate={onNavigate} />
      <main className="styleguide">
        <header className="styleguide__intro">
          <p className="mono muted small">chat-stream-harness / styleguide</p>
          <h1>Design system</h1>
          <p className="muted">
            The reference world is the strip chart recorder and the protocol analyser: instruments
            that draw a continuous trace and mark where the signal broke. Paper and ink neutrals,
            hairline rules in place of shadows, and exactly three state colours, each with one
            meaning.
          </p>
        </header>

        <Section title="Colour">
          <h3>Surface</h3>
          <SwatchRow tokens={SURFACE_TOKENS} />
          <h3>Ink</h3>
          <SwatchRow tokens={INK_TOKENS} />
          <h3>State</h3>
          <p className="muted small">
            Signal is stream flow and nothing else. Hold is waiting. Fault is failure. No colour
            carries two meanings, and nothing else in the product is coloured at all.
          </p>
          <SwatchRow tokens={STATE_TOKENS} />
        </Section>

        <Section title="Type">
          <div className="styleguide__type-scale">
            {TYPE_STEPS.map((step) => (
              <div key={step.token} className="styleguide__type-row">
                <span className="mono muted small styleguide__type-token">{step.token}</span>
                <span style={{ fontSize: `var(${step.token})` }}>
                  Resume replays from event <code>89</code>
                </span>
                <span className="muted small styleguide__type-use">{step.use}</span>
              </div>
            ))}
          </div>
          <div className="panel panel--sunk styleguide__prose-demo">
            <p className="small">
              Prose is Instrument Sans. Every identifier, count, and duration is IBM Plex Mono: the
              stream <code>str_000042</code> reconnected after <code>412</code> tokens and resumed
              from event <code>89</code> in <code>1.24s</code>. The two faces never mix inside one
              label.
            </p>
          </div>
        </Section>

        <Section title="Rhythm">
          <div className="styleguide__rhythm">
            {SPACE_STEPS.map((token) => (
              <div key={token} className="styleguide__rhythm-row">
                <span className="mono muted small styleguide__rhythm-token">{token}</span>
                <div className="styleguide__rhythm-bar" style={{ width: `var(${token})` }} />
              </div>
            ))}
          </div>
        </Section>

        <Section title="Controls">
          <div className="styleguide__row">
            <button type="button" className="button button--primary">
              Send
            </button>
            <button type="button" className="button">
              Replay scenario
            </button>
            <button type="button" className="button button--quiet">
              Compare with server
            </button>
            <button type="button" className="button" disabled>
              Send
            </button>
          </div>
          <div className="styleguide__row">
            <label className="muted small" id="styleguide-scenario-label">
              Scenario
            </label>
            <ScenarioSelect
              labelId="styleguide-scenario-label"
              value={scenarioValue}
              options={DEMO_SCENARIOS}
              onChange={setScenarioValue}
            />
            <input
              className="field styleguide__demo-input"
              placeholder="Ask something worth interrupting"
              aria-label="Message"
            />
          </div>
        </Section>

        <Section title="Range and switch">
          <p className="muted small">
            Added for the settings tab's knob controls. The track is a flat hairline in{' '}
            <code>--rule</code>, the same no-gradient rule the rest of the system holds; only the
            thumb takes <code>--signal</code>, so a slider reads as one more instrument control
            rather than as UI chrome borrowed from elsewhere.
          </p>
          <div className="styleguide__range-demo">
            <label className="styleguide__range-label">
              <span className="muted small">
                Tokens per second: <span className="mono">{rangeValue}</span>
              </span>
              <input
                type="range"
                className="range"
                min={1}
                max={1000}
                step={1}
                value={rangeValue}
                onChange={(event) => {
                  setRangeValue(Number(event.target.value));
                }}
                aria-label="Example range"
              />
            </label>
            <label className="switch">
              <input
                type="checkbox"
                checked={switchOn}
                onChange={(event) => {
                  setSwitchOn(event.target.checked);
                }}
              />
              <span className="switch__track">
                <span className="switch__thumb" />
              </span>
              <span className="switch__label">Seeded jitter</span>
            </label>
          </div>
        </Section>

        <Section title="Loading state">
          <p className="muted small">
            While a response is connecting or thinking, three skeleton lines stand in for the prose
            that has not arrived yet. Solid <code>--paper-sunk</code> fill with a hairline border,
            an opacity pulse rather than a gradient sweep, so the no-gradient rule holds here too.
          </p>
          <div className="message__waiting styleguide__loading-demo">
            <p className="muted message__waiting-label">Thinking.</p>
            <div className="skeleton" aria-hidden="true">
              <span className="skeleton__line" />
              <span className="skeleton__line" />
              <span className="skeleton__line" />
            </div>
          </div>
        </Section>

        <Section title="Stream phases">
          <p className="muted small">
            One badge per machine phase. Colour follows meaning: signal while output flows, hold
            while waiting, fault on failure, plain ink for rest states.
          </p>
          <div className="styleguide__row">
            {PHASE_BADGES.map(({ phase, tone }) => (
              <span key={phase} className={`badge${tone === 'quiet' ? '' : ` badge--${tone}`}`}>
                {phase}
              </span>
            ))}
          </div>
        </Section>

        <Section title="Notices">
          <div className="notice notice--signal" role="presentation">
            <span className="notice__title">Content replaced</span>
            <p>
              The text above was withheld by a content check and replaced. Categories:{' '}
              <code>harmful</code>. <code>412</code> characters were removed.
            </p>
          </div>
          <div className="notice notice--hold" role="presentation">
            <span className="notice__title">Queued</span>
            <p>
              Position <code>7</code>, estimated wait <code>45s</code>.
            </p>
            <div>
              <button type="button" className="button">
                Cancel
              </button>
            </div>
          </div>
          <div className="notice notice--hold" role="presentation">
            <span className="notice__title">Output lost</span>
            <p>
              Events <code>90</code> to <code>92</code> could not be recovered after three attempts.
              The text before and after this point is intact; the gap is marked, not smoothed over.
            </p>
          </div>
        </Section>

        <Section title="Error treatments">
          <p className="muted small">
            Ten codes, ten treatments. Retryable codes get a retry control;{' '}
            <code>content_blocked</code> and <code>context_too_large</code> do not, because retrying
            cannot change the answer.
          </p>
          <div className="styleguide__error-list">
            {ERROR_PREVIEWS.map((preview) => (
              <ErrorNotice key={preview.code} preview={preview} />
            ))}
          </div>
        </Section>

        <Section title="Values">
          <div className="panel styleguide__values-panel">
            <div className="kv">
              <span className="kv__key">phase</span>
              <span className="kv__value">streaming</span>
            </div>
            <div className="kv">
              <span className="kv__key">last event id</span>
              <span className="kv__value">412</span>
            </div>
            <div className="kv">
              <span className="kv__key">reconnects</span>
              <span className="kv__value">2</span>
            </div>
            <div className="kv kv--last">
              <span className="kv__key">tokens per second</span>
              <span className="kv__value">61.4</span>
            </div>
          </div>
        </Section>
      </main>
    </>
  );
}
